import React, { useContext } from 'react';
import Cookies from 'js-cookie';
import RawHTML from '@components/Common/RawHTML/index';
import Layout from '../layouts/Layout/index';
import CrioButton from '../CrioButton/index';
import LeadGenerationButton from '@components/LeadGenerationForm/LeadGenerationButton';
import { useCustomToast } from '@components/extra/useCustomToast';
import useSendActivities from '@src/hooks/useSendActivities';
import { GTM } from '@src/analytics/gtm';
import { gtmEvents } from '@src/analytics/gtmEventsConstant';
import { ELeadSquaredActivityCode } from '@src/constants/leadsquaredActivityConstants/index';
import {
  GlobalStateContext,
  GlobalDispatchContext,
} from '@src/context/GlobalContextProvider';
import {
  LEAD_GEN_FINAL_CTA,
  LEAD_GEN_FINAL_CTA_CALLBACK,
} from '@src/constants/LeadGenerationConstants/index';

// Generic fallback content — used when a page renders <FinalCTA /> without
// wiring it to YAML (or when the `finalCta` block is missing from the YAML).
export const DEFAULT_FINAL_CTA = {
  eyebrow: 'Your career starts here',
  title:
    'Ready to <span class="text-v5-green-300">start your journey?</span>',
  description:
    'Book a free trial. Meet a mentor. Walk away with a personalised learning plan — no fee, no commitment.',
  primaryCtaText: 'Book Your Trial, Now',
  secondaryCtaText: 'Request a Callback',
  footerNote: '',
};

export default function FinalCTA({ data, className }) {
  const state = useContext(GlobalStateContext);
  const dispatch = useContext(GlobalDispatchContext);
  const { sendActivityHandler } = useSendActivities();

  const triggerSuccessToast = useCustomToast(
    'success',
    <div className="">
      <h2 className="font-base font-rubik text-lg">Success!</h2>
      <p className="font-rubik text-xs">
        Thanks! We will give you a call or email you between 9 AM to 9 PM
      </p>
    </div>,
  );

  const {
    eyebrow,
    title,
    description,
    primaryCtaText,
    secondaryCtaText,
    footerNote,
  } = data && data.title ? data : DEFAULT_FINAL_CTA;

  // Callback form submit — mirrors the "Request Call Back" flow used elsewhere.
  const onCallbackSubmit = (email, phone) => {
    GTM.login({
      userId: email,
      email: email,
      phone: phone,
      whatsappNumber: phone,
    });
    GTM.setEmail(email);

    if (!state.isEmailEventSubmitted) {
      GTM.track(gtmEvents.EMAIL_SUBMITTED, {
        type: 'CALL_ME_POPUP',
      });
      dispatch({
        type: 'SET_IS_EMAIL_EVENT_SUBMITTED',
        payload: true,
      });
    }

    sendActivityHandler(ELeadSquaredActivityCode.CALL_ME_BACK_SUBMITTED);

    window.webengage &&
      window.webengage.user.setAttribute('we_whatsapp_opt_in', true);

    if (Cookies.get('isPhoneVerified') === 'true') triggerSuccessToast();
  };

  return (
    <Layout id="cta" className={className}>
      <div className="container relative z-[999] text-center">
        {eyebrow && (
          <span className="mb-5 inline-flex items-center gap-2 rounded-full border border-v5-green-200 px-4 py-1 text-xs font-bold uppercase tracking-wide text-v5-green-300">
            <span className="h-1.5 w-1.5 rounded-full bg-v5-green-300"></span>
            {eyebrow}
          </span>
        )}

        {title && (
          <h2 className="z-[300] mb-5 font-manrope font-extrabold capitalize leading-snug md:px-32">
            <RawHTML>{title}</RawHTML>
          </h2>
        )}

        {description && (
          <p className="mb-10 font-light leading-loose text-v5-neutral-400 sm:px-44">
            <RawHTML>{description}</RawHTML>
          </p>
        )}

        <div className="flex flex-wrap items-stretch justify-center gap-x-3 gap-y-3">
          <LeadGenerationButton
            type={primaryCtaText || 'Book Your Trial, Now'}
            text={primaryCtaText}
            buttonLocation={LEAD_GEN_FINAL_CTA}
          />
          {secondaryCtaText && (
            <LeadGenerationButton
              type={secondaryCtaText}
              buttonLocation={LEAD_GEN_FINAL_CTA_CALLBACK}
              isOnePageForm
              formHeading="Request Callback"
              pageOneButtonText="Call me back"
              onSubmitPageOne={onCallbackSubmit}
              clickEvent={() => {
                GTM.track(gtmEvents.CALLME_OPENED);
                sendActivityHandler(
                  ELeadSquaredActivityCode.REQUEST_CALL_BACK_CLICKED,
                );
              }}
              buttonComponent={
                <CrioButton variant="secondary">{secondaryCtaText}</CrioButton>
              }
            />
          )}
        </div>

        {footerNote && (
          <p className="mx-auto mt-6 max-w-xl text-center text-sm text-v5-neutral-400">
            <RawHTML>{footerNote}</RawHTML>
          </p>
        )}
      </div>
    </Layout>
  );
}
